import { Injectable } from '@angular/core';
import { Theme } from './theme';
import { TextZoom } from '@capacitor/text-zoom';
import { AppStateService } from '../app-state/app-state.service';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  constructor(appStateService: AppStateService) {
    if (appStateService.isNative) {
      appStateService.show
        .pipe(
          switchMap(() =>
            fromPromise(
              TextZoom.getPreferred().catch((err) => {
                console.warn('TextZoom.getPreferred() failed:', err);
                return { value: 1 }; // fallback to default zoom factor
              }),
            ),
          ),
        )
        .subscribe(({ value }) => {
          this.setTextZoomFactor(value);
        });
    }
  }

  applyTheme(theme: Theme) {
    const propertyNameMapping = {
      fontFamily: '--kgr-font-family-base',
      fontFamilyHeadlines: '--kgr-headings-font-family',
      colorWhite: '--kgr-color-white',
      colorBlack: '--kgr-color-black',
      colorGrayDarker: '--kgr-color-gray-darker',
      colorGrayDark: '--kgr-color-gray-dark',
      colorGray: '--kgr-color-gray',
      colorGrayMedium: '--kgr-color-gray-medium',
      colorGrayLight: '--kgr-color-gray-light',
      colorGrayLighter: '--kgr-color-gray-lighter',
      colorAccent: '--kgr-color-accent',
      colorAccentAlternate: '--kgr-color-accent-alternate',
      colorAccentInverted: '--kgr-color-accent-inverted',
      colorSuccess: '--kgr-color-success',
      colorSuccessInverted: '--kgr-color-success-inverted',
      colorWarning: '--kgr-color-warning',
      colorWarningInverted: '--kgr-color-warning-inverted',
      colorError: '--kgr-color-error',
      colorErrorInverted: '--kgr-color-error-inverted',
      colorHighlight: '--kgr-color-highlight',
      colorHighlightInverted: '--kgr-color-highlight-inverted',
    };

    Object.entries(propertyNameMapping).forEach(([jsPropertyName, cssPropertyName]) => {
      this.setCssCustomProperty(cssPropertyName, theme[jsPropertyName]);
    });

    this.setCssCustomProperty('--kgr-inputGroup-label-text-transform', theme.formLabelUppercase ? 'uppercase' : 'none');

    if (theme.fontSize) {
      this.setCssCustomProperty('--kgr-font-size-base', `${theme.fontSize}px`);
    }

    const existingThemeFontClass = 'kgr-font--';
    for (const className of document.documentElement.classList.values()) {
      if (className.startsWith(existingThemeFontClass)) {
        document.documentElement.classList.remove(className);
      }
    }

    if (theme.fontFamilyNormalized) {
      document.documentElement.classList.add(`kgr-font--${theme.fontFamilyNormalized}`);
    }
    if (theme.fontFamilyHeadlinesNormalized) {
      document.documentElement.classList.add(`kgr-font--${theme.fontFamilyHeadlinesNormalized}Headings`);
    }

    const $themeColor = document.querySelector('meta[name="theme-color"]');
    if ($themeColor) {
      if (theme.colorAccent) {
        $themeColor.setAttribute('content', theme.colorAccent);
      } else {
        $themeColor.setAttribute('content', '#000870');
      }
    }
  }

  private setTextZoomFactor(zoomFactor: number): void {
    zoomFactor = Math.min(1.3, zoomFactor); // restrict max zoom factor to 1.3
    this.setCssCustomProperty('--kgr-zoom-factor', `${zoomFactor}`);
  }

  setCssCustomProperty(propertyName: string, value?: string): void {
    const style = document.documentElement.style;
    if (!style) {
      console.log('Cannot apply theme due to missing CSS Custom Properties Browser support');

      return;
    }

    if (value) {
      style.setProperty(propertyName, value);
    } else {
      style.removeProperty(propertyName);
    }
  }

  hideTabbar() {
    this.setCssCustomProperty('--kgr-pageLayout-footer-height', '0px');
    this.setCssCustomProperty('--kgr-tabbar-height', '0px');
  }

  unHideTabbar() {
    this.setCssCustomProperty('--kgr-pageLayout-footer-height', null);
    this.setCssCustomProperty('--kgr-tabbar-height', null);
  }
}
